/**
 * M-WF-01 · Compila Orari — metodo alternativo di inserimento del Cartellino, come il foglio presenze cartaceo.
 * Workflow: Compila → Verifica automatica → Anteprima modifiche → Conferma → Cartellino aggiornato.
 * Il Cartellino resta l'unica fonte ufficiale: la scrittura avviene solo dal backend, tramite il servizio
 * del Cartellino, e solo per le giornate scelte esplicitamente in anteprima.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Keyboard } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { SuiteButton, SuiteEmployeePicker, SuiteMonthPicker } from "@/components/suite";
import { useCompanySelection } from "@/foundation/context/ActiveCompanyContext";
import { useFoundationDraft } from "@/foundation/drafts/useFoundationDraft";
import { TimesheetGrid, TimesheetTotalsBar, buildMonthDays, prefillEmptyDays, copyFromOffset, applyPaste, serializeDays, evaluateMonth, emptyDay, applyTimesheetCopy, DEFAULT_INTERVAL_COUNT } from "@/foundation/timesheet";
import { CopyFromDialog } from "@/foundation/copy";
import { WorkflowStepper } from "@/foundation/workflow";
import { SequenceProgress, NextSubjectPanel, lastSubject } from "@/foundation/sequence";
import { automaticDecisions } from "@/foundation/review";
import { formatDateIT } from "@/foundation/formatting";
import { TimesheetActionBar } from "@/components/timesheet/TimesheetActionBar";
import { TimesheetVerifyPanel } from "@/components/timesheet/TimesheetVerifyPanel";
import { TimesheetReview } from "@/components/timesheet/TimesheetReview";
import { TimesheetAppliedBanner } from "@/components/timesheet/TimesheetAppliedBanner";
import { TimesheetCompareDialog } from "@/components/timesheet/TimesheetCompareDialog";
import { useTimesheetCopy } from "@/components/timesheet/useTimesheetCopy";
import { useTimesheetSequence, TIMESHEET_WORKFLOW } from "@/components/timesheet/useTimesheetSequence";

const STEPS = [{ id: "compile", label: "Compila" }, { id: "verify", label: "Verifica" }, { id: "review", label: "Anteprima" }, { id: "confirm", label: "Conferma" }, { id: "applied", label: "Cartellino aggiornato" }];
const STEP_INDEX = { compile: 0, verify: 1, review: 2, applying: 3, applied: 5 };
const fullName = (e) => (e ? `${e.last_name || ""} ${e.first_name || ""}`.trim() : "");

export default function CompilaOrari() {
  const [params, setParams] = useSearchParams();
  const routerNavigate = useNavigate();
  const [companyId, setCompanyId] = useCompanySelection();
  const employeeId = params.get("employee") || "";
  const now = new Date();
  const year = Number(params.get("year")) || now.getFullYear();
  const month = Number(params.get("month")) || now.getMonth() + 1;
  const [employees, setEmployees] = useState([]);
  const [causes, setCauses] = useState([]);
  const [data, setData] = useState(null);
  const [days, setDays] = useState([]);
  const [draftStatus, setDraftStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("compile");
  const [review, setReview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [qualifications, setQualifications] = useState({});
  const [busy, setBusy] = useState(false);
  const [nextOpen, setNextOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [compare, setCompare] = useState(null);
  const [activeRow, setActiveRow] = useState(null);

  const saveFn = useCallback(async (payload, version) => {
    const { data: res } = await api.put("/timesheet/draft", { employee_id: employeeId, year, month, version, days: payload });
    setDraftStatus(res.status);
    return res;
  }, [employeeId, year, month]);
  const applied = data && !data.draft ? data.applied : null;
  const draft = useFoundationDraft({ save: saveFn, enabled: !!employeeId && !data?.read_only && !applied });

  useEffect(() => {
    api.get("/employees", { params: { status: "all", ...(companyId ? { company_id: companyId } : {}) } })
      .then(({ data: rows }) => setEmployees([...rows].sort((a, b) => fullName(a).localeCompare(fullName(b), "it"))))
      .catch((e) => toast.error(apiErr(e)));
    api.get("/causes").then(({ data: rows }) => setCauses(rows.filter((c) => c.active !== false))).catch(() => {});
  }, [companyId]);
  const justificationOptions = useMemo(() => causes.filter((c) => c.behavior && c.behavior.counts_as_work === false)
    .map((c) => ({ code: c.code, label: c.label || c.code, short: c.display_code || c.code })), [causes]);
  const causeLabel = useCallback((code) => causes.find((c) => c.code === code)?.display_code || code, [causes]);

  const seq = useTimesheetSequence({ employees, employeeId, year, month, companyId });

  const goParams = useCallback(async (patch) => {
    await draft.flush();
    setParams((p) => { const n = new URLSearchParams(p); Object.entries(patch).forEach(([k, v]) => n.set(k, String(v))); return n; });
  }, [draft, setParams]);

  useEffect(() => {
    if (employeeId || !employees.length) return;
    const last = lastSubject(TIMESHEET_WORKFLOW, companyId);
    if (last && employees.some((e) => e.id === last)) goParams({ employee: last });
  }, [employees, employeeId, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!employeeId) { setData(null); setDays([]); return; }
    setLoading(true);
    try {
      const { data: res } = await api.get("/timesheet/month", { params: { employee_id: employeeId, year, month } });
      const source = res.draft || res.applied;
      // Collegamento diretto (?employee=): allinea l'azienda di lavoro a quella del dipendente autorizzato.
      if (companyId && res.employee.company_id && res.employee.company_id !== companyId) setCompanyId(res.employee.company_id);
      setData(res);
      setDays(buildMonthDays({ year, month, draftDays: source?.payload?.days, planned: res.planned, mode: source ? "empty" : res.prefill_mode }));
      setDraftStatus(res.draft?.status || null);
      setStep(!res.draft && res.applied ? "applied" : "compile");
      setReview(null);
      draft.reset(res.draft?.version, res.draft?.updated_at);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [employeeId, year, month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const meta = useMemo(() => Object.fromEntries((data?.cartellino || []).map((d) => [d.date, {
    holiday: d.holiday,
    expectedMinutes: d.holiday || d.outside_employment ? 0 : Math.round((d.theoretical || 0) * 60),
    cartellinoHasData: Object.values(d.cause_hours || {}).some((v) => Number(v) > 0),
    protected: ["approved", "locked"].includes(d.status) && !d.holiday,
  }])), [data]);
  const { evaluations, totals } = useMemo(() => evaluateMonth(days, meta), [days, meta]);
  const readOnly = !!data?.read_only || !!applied;

  const commit = useCallback((next) => { setDays(next); draft.schedule(serializeDays(next)); }, [draft]);
  const onDayChange = useCallback((date, day) => setDays((prev) => {
    const next = prev.map((d) => (d.date === date ? day : d));
    draft.schedule(serializeDays(next));
    return next;
  }), [draft]);

  const copy = useTimesheetCopy({ open: copyOpen, days, meta, employeeId, year, month, employees });

  const jumpToIssue = (level) => {
    const date = days.find((d) => evaluations[d.date]?.issues.some((i) => i.level === level))?.date;
    document.querySelector(`[data-testid="ts-${date}-in-1"]`)?.focus();
  };

  const verify = async () => {
    if (totals.errors) { toast.error(`Ci sono ${totals.errors} anomalie bloccanti da correggere prima della conferma`); jumpToIssue("error"); return; }
    setBusy(true);
    try {
      if (!(await draft.flush()) && draft.status === "conflict") return;
      if (!draft.version && !draft.hasPending()) { const r = await saveFn(serializeDays(days), null); draft.reset(r.version, r.updated_at); }
      const { data: res } = await api.post("/timesheet/draft/confirm", { employee_id: employeeId, year, month });
      setDraftStatus(res.status);
      draft.reset(res.version, res.confirmed_at);
      setReview(res);
      setDecisions(automaticDecisions(res.preview.map((r) => ({ ...r, key: r.date }))));
      setQualifications({});
      if (!res.preview.length) toast.info("Nessuna giornata compilata da trasferire");
      setStep(Object.keys(res.warnings || {}).length ? "verify" : "review");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const afterApply = async () => {
    await seq.refresh();
    const mode = data?.next_mode || "ask";
    if (mode === "cartellino") { routerNavigate(`/presenze?employee=${employeeId}&year=${year}&month=${month}`); return; }
    setNextOpen(true);
  };
  useEffect(() => {
    if (!nextOpen || data?.next_mode !== "auto" || !seq.state.next) return;
    setNextOpen(false);
    toast.success(`Passo a ${fullName(seq.state.next)}`);
    goParams({ employee: seq.state.next.id });
  }, [nextOpen, seq.state.next]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    setStep("applying");
    setBusy(true);
    try {
      const { data: res } = await api.post("/timesheet/draft/apply", { employee_id: employeeId, year, month, version: draft.version, decisions, qualifications });
      toast.success(`Cartellino aggiornato: ${res.counts.update} giornate`);
      await load();
      await afterApply();
    } catch (e) { toast.error(apiErr(e)); setStep("review"); } finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm("Scartare la bozza del mese? Il Cartellino non viene modificato.")) return;
    try {
      await api.post("/timesheet/draft/discard", { employee_id: employeeId, year, month });
      toast.success("Bozza scartata");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/timesheet/draft/reopen", { employee_id: employeeId, year, month });
      toast.success(`Revisione v${res.revision} aperta`);
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const openCompare = async () => {
    try { setCompare((await api.get("/timesheet/compare", { params: { employee_id: employeeId, year, month } })).data); } catch (e) { toast.error(apiErr(e)); }
  };

  const toQualify = (review?.preview || []).filter((r) => r.needs_qualification && !qualifications[r.date]
    && (decisions[r.date] || r.default_decision) === "update").length;
  const current = employees.find((e) => e.id === employeeId) || (data?.employee?.id === employeeId ? data.employee : null);
  return (
    <div className="max-w-[1500px] space-y-4" data-testid="compila-orari-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-suite-neutral-500">Foglio presenze · {new Date(year, month - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</div>
          <h1 className="text-3xl font-semibold tracking-tight text-suite-neutral-900" data-testid="timesheet-title">{current ? fullName(current) : "Compila Orari"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SequenceProgress state={seq.state} onPrev={() => seq.neighbour(-1) && goParams({ employee: seq.neighbour(-1).id })} onNext={() => seq.neighbour(1) && goParams({ employee: seq.neighbour(1).id })} testid="timesheet-sequence" />
          <SuiteEmployeePicker employees={employees} value={employeeId} onValueChange={(v) => goParams({ employee: v })} testId="timesheet-employee-picker" />
          <SuiteMonthPicker year={year} month={month} size="sm" testid="timesheet-month-picker" onChange={({ year: y, month: m }) => goParams({ year: y, month: m })} />
          <Link to={`/presenze?employee=${employeeId}&year=${year}&month=${month}`} data-testid="timesheet-back-link">
            <SuiteButton size="sm" variant="ghost" icon={ArrowLeft}>Cartellino</SuiteButton>
          </Link>
        </div>
      </div>

      {!employeeId && <div className="rounded-lg border border-dashed p-10 text-center text-sm text-suite-neutral-500" data-testid="timesheet-no-employee">Seleziona un dipendente.</div>}
      {loading && <div className="text-sm text-suite-neutral-500" data-testid="timesheet-loading">Caricamento…</div>}
      {data && !loading && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-suite-neutral-200 pb-3">
            <WorkflowStepper steps={STEPS} current={STEP_INDEX[step]} testid="timesheet-stepper" />
          </div>
          {applied && <TimesheetAppliedBanner applied={applied} revisions={data.revisions} readOnly={data.read_only} onCompare={openCompare} onReopen={reopen} reopening={busy} />}
          {(step === "compile" || step === "applied") && (
            <>
              {!applied && <TimesheetActionBar data={data} draft={draft} draftStatus={draftStatus} readOnly={readOnly} verifying={busy}
                canPrefill={!!Object.keys(data.planned || {}).length} onPrefill={() => commit(prefillEmptyDays(days, data.planned))}
                onCopy={() => setCopyOpen(true)} onDiscard={discard} onVerify={verify} onReload={load} />}
              <TimesheetGrid days={days} meta={meta} evaluations={evaluations} readOnly={readOnly} justificationOptions={justificationOptions}
                onDayChange={onDayChange} onActiveRow={setActiveRow} onOpenCopy={() => !readOnly && setCopyOpen(true)}
                onCopyFrom={(row, offset) => commit(copyFromOffset(days, row, offset))}
                onClearDay={(row) => commit(days.map((d, i) => (i === row ? emptyDay(d.date, d.intervals.length) : d)))}
                onPasteGrid={(row, col, matrix) => commit(applyPaste(days, row, col, matrix))} />
              <TimesheetTotalsBar totals={totals} onJumpToIssue={jumpToIssue} />
            </>
          )}
          {step === "verify" && review && <TimesheetVerifyPanel warnings={review.warnings} onBack={() => setStep("compile")} onContinue={() => setStep("review")} />}
          {(step === "review" || step === "applying") && review && (
            <div className="space-y-3" data-testid="timesheet-review-step">
              <div className="text-sm text-suite-neutral-600">Controlla cosa cambierà nel Cartellino. Le giornate già compilate restano invariate finché non scegli <b>Aggiorna</b>; le giornate protette non vengono mai modificate.</div>
              <TimesheetReview rows={review.preview} decisions={decisions} onChange={setDecisions} qualifications={qualifications} onQualify={setQualifications} causeLabel={causeLabel} />
              {toQualify > 0 && <div className="text-sm text-orange-700" data-testid="timesheet-to-qualify">{toQualify} {toQualify === 1 ? "giornata con eccedenza da qualificare" : "giornate con eccedenza da qualificare"} prima della conferma.</div>}
              <div className="flex justify-between">
                <SuiteButton variant="ghost" icon={ArrowLeft} onClick={() => setStep("compile")} data-testid="timesheet-review-back">Torna alla compilazione</SuiteButton>
                <SuiteButton iconRight={ArrowRight} loading={busy} onClick={apply} disabled={!review.preview.length || toQualify > 0} data-testid="timesheet-apply-btn">Conferma e aggiorna il Cartellino</SuiteButton>
              </div>
            </div>
          )}
        </>
      )}
      {step === "compile" && data && !readOnly && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-suite-neutral-500" data-testid="timesheet-shortcuts">
          <Keyboard size={13} /> <span><b>830</b> → 08:30</span><span><b>Invio</b> campo successivo</span><span><b>↑ ↓</b> giorno</span>
          <span><b>F9</b> copia giorno precedente</span><span><b>Shift+F9</b> copia settimana precedente</span><span><b>Alt+C</b> Copia da…</span><span><b>Shift+Canc</b> svuota giorno</span><span><b>Ctrl+V</b> incolla da Excel</span>
        </div>
      )}
      <CopyFromDialog open={copyOpen} onOpenChange={setCopyOpen} sources={copy.sources} buildPlan={copy.buildPlan} dates={days.map((d) => d.date)}
        initialDates={activeRow !== null && days[activeRow] ? [days[activeRow].date] : []} formatDate={formatDateIT}
        options={[{ id: "withJustifications", label: "Copia anche i giustificativi" }]}
        onApply={(plan) => { commit(applyTimesheetCopy(days, plan, DEFAULT_INTERVAL_COUNT)); toast.success(`${plan.changes.length} giornate copiate`); }} />
      <TimesheetCompareDialog compare={compare} onClose={() => setCompare(null)} causeLabel={causeLabel} />
      <NextSubjectPanel open={nextOpen && (data?.next_mode !== "auto" || !seq.state.next)} onOpenChange={setNextOpen}
        title="Cartellino aggiornato" summary={`Compilazione di ${fullName(current)} applicata.`} state={seq.state} nameOf={fullName} currentName={fullName(current)}
        onNext={() => { setNextOpen(false); goParams({ employee: seq.state.next.id }); }} onStay={() => setNextOpen(false)}
        onReturn={() => routerNavigate(`/presenze?employee=${employeeId}&year=${year}&month=${month}`)} returnLabel="Torna al Cartellino" />
    </div>
  );
}
